import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

const guidesList = [
  { title: 'AllChinaBuy Spreadsheet Complete Guide', icon: 'ri-file-list-3-line', color: 'bg-primary-50 text-primary-600', href: '/en/spreadsheet/' },
  { title: 'How to Use AllChinaBuy Spreadsheet', icon: 'ri-table-2', color: 'bg-secondary-50 text-secondary-600', href: '/en/spreadsheet/' },
  { title: 'AllChinaBuy Product Research Guide', icon: 'ri-search-2-line', color: 'bg-accent-50 text-accent-600', href: '/en/guides/' },
  { title: 'How to Find Reliable Sellers', icon: 'ri-user-search-line', color: 'bg-primary-50 text-primary-600', href: '/en/guides/' },
  { title: 'AllChinaBuy QC Guide: How to Check Photos', icon: 'ri-file-search-line', color: 'bg-secondary-50 text-secondary-600', href: '/en/guides/' },
  { title: 'AllChinaBuy Shipping Cost Guide', icon: 'ri-truck-line', color: 'bg-accent-50 text-accent-600', href: '/en/shipping/' },
  { title: 'How to Choose Shipping Methods', icon: 'ri-plane-line', color: 'bg-primary-50 text-primary-600', href: '/en/shipping/' },
  { title: 'AllChinaBuy Coupon & Discount Guide', icon: 'ri-coupon-3-line', color: 'bg-secondary-50 text-secondary-600', href: '/en/coupon/' },
  { title: 'Is AllChinaBuy Legit? Safety Guide', icon: 'ri-shield-check-line', color: 'bg-accent-50 text-accent-600', href: '/en/safety/' },
  { title: 'AllChinaBuy Beginner Buying Guide', icon: 'ri-book-open-line', color: 'bg-primary-50 text-primary-600', href: '/en/guides/' },
  { title: 'AllChinaBuy vs Superbuy Comparison', icon: 'ri-arrow-left-right-line', color: 'bg-secondary-50 text-secondary-600', href: '/en/review/' },
  { title: 'AllChinaBuy Alternatives Guide', icon: 'ri-apps-line', color: 'bg-accent-50 text-accent-600', href: '/en/review/' },
];

const faqs = [
  { q: 'How do I start using the AllChinaBuy spreadsheet?', a: 'Begin by navigating to the spreadsheet section on our homepage. Select your desired category, browse through product listings, and click through to kaspreadsheet.com for detailed product information and purchasing options. The spreadsheet is designed to be beginner-friendly with clear category filters.' },
  { q: 'What information does each spreadsheet entry include?', a: 'Each entry in the AllChinaBuy spreadsheet typically includes product name, category, price in both Chinese Yuan and US Dollars, product images, and a direct link to the product page on the main website. Some entries also include seller notes, size availability, and quality indicators.' },
  { q: 'How often is the spreadsheet updated?', a: 'We update the spreadsheet regularly with new product releases, price changes, and category additions. We recommend checking back weekly for the latest additions to shoes, hoodies, t-shirts, and other popular categories.' },
  { q: 'Can I find sellers directly through the spreadsheet?', a: 'Yes, the spreadsheet includes seller information where available. You can also visit our Sellers section to learn how to evaluate seller reliability, check ratings, and verify authenticity before making a purchase.' },
];

export default function GuidesPage() {
  const { t, i18n } = useTranslation();
  const { lang = 'en' } = useParams<{ lang: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  const handleGuideClick = (href: string) => {
    if (href.startsWith('/en/')) {
      const target = href.replace('/en/', `/${lang}/`);
      navigate(target);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-20 md:pt-24 pb-4 bg-white border-b border-background-200/70">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <a href={`/${lang}/`} className="hover:text-primary-600 transition-colors">{t('breadcrumb.home')}</a>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-700">{t('nav.guides')}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-white py-16 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
            AllChinaBuy Guides & Tutorials
          </h1>
          <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
            Explore comprehensive guides for using the AllChinaBuy spreadsheet, finding reliable sellers, reading QC photos, calculating shipping costs, comparing agents, and navigating the full buying process safely.
          </p>
        </div>
      </section>

      {/* Intro Text */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="prose-content">
            <h2>What Are AllChinaBuy Guides?</h2>
            <p>
              Our guides are designed to help both newcomers and experienced buyers navigate the AllChinaBuy ecosystem effectively. Whether you are trying to understand how the spreadsheet works, comparing shipping options, or verifying seller trustworthiness, these resources provide step-by-step instructions and practical advice.
            </p>
            <p>
              All guides are written independently based on real user experiences and research. We do not represent AllChinaBuy officially, but we strive to provide accurate, up-to-date information that makes your buying journey smoother and safer.
            </p>
            <h2>How to Use These Guides</h2>
            <p>
              Start with the <strong>AllChinaBuy Spreadsheet Complete Guide</strong> if you are new to the platform. It covers everything from navigating the spreadsheet to understanding pricing, categories, and how to find specific products. For shipping questions, check the shipping cost guide. For safety concerns, read our safety and legitimacy guides before placing any orders.
            </p>
          </div>
        </div>
      </section>

      {/* Guide Cards Grid */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-main">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            All Available Guides
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {guidesList.map((guide, idx) => (
              <div
                key={idx}
                onClick={() => handleGuideClick(guide.href)}
                className="group cursor-pointer bg-background-50 rounded-xl p-6 border border-background-200/60 hover:border-primary-300 hover:shadow-lg transition-all duration-300"
              >
                <div className={`w-12 h-12 rounded-xl ${guide.color} flex items-center justify-center mb-4`}>
                  <i className={`${guide.icon} text-xl`}></i>
                </div>
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                  {guide.title}
                </h3>
                <p className="text-foreground-500 text-sm mb-4">
                  Click to read the full guide and learn practical tips for navigating AllChinaBuy resources.
                </p>
                <span className="inline-flex items-center text-primary-600 text-sm font-medium">
                  Read Guide <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Spreadsheet CTA */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="bg-white rounded-2xl p-8 md:p-10 border border-background-200/70 text-center">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              Ready to Explore the Spreadsheet?
            </h3>
            <p className="text-foreground-600 mb-6 leading-relaxed">
              Our guides are most effective when paired with hands-on exploration. Visit the AllChinaBuy spreadsheet to browse real products, compare prices, and start your research journey today.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={`/${lang}/spreadsheet/`}
                className="btn-primary inline-flex items-center justify-center"
              >
                AllChinaBuy Spreadsheet
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="btn-secondary inline-flex items-center justify-center"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-main max-w-3xl">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            Frequently Asked Questions About Guides
          </h2>
          <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-background-200/60 rounded-xl bg-background-50 overflow-hidden" itemScope itemType="https://schema.org/Question">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 md:p-5 text-left"
                >
                  <span className="font-medium text-foreground-900 text-sm md:text-base" itemProp="name">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-lg text-primary-500 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-4 md:px-5 pb-4 md:pb-5" itemScope itemType="https://schema.org/Answer">
                    <p className="text-foreground-600 text-sm leading-relaxed" itemProp="text">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}